// Stage 2 "Dashboard" · 2.3 (c) — Fee, cost and over-run (Partner / Admin only): one row per engagement, one total row PER
// CURRENCY (never SAR + USD added), the firm-level AI use (no engagement) as one separate line. AI use = units + USD, no provider.
// Field names as backend/firm_dashboard.py sends them (budgeted_hours, hours_spent, cost, margin, overrun.status,
// ai.{units, calls, usd}); totals_by_currency keyed by currency text or "unspecified".
// SUNDAY BATCH:
//   D-2  margin_status "incomplete" (hours without a rate) → "Incomplete - n h without rate", never a margin figure (row + total)
//   D-3  fee_set false / fee null → "Fee not set" (row); a total with unset fees: the sum of the set fees + "Fee not set for n"
//   D-6  hours through fdTheme.fmtHours (below 0.1 h in minutes)
//   D-8  usd null → "rate not set" (the units are not repeated as a cost); the configured USD rate per unit on the firm line
//   D-9  the firm AI total (engagements + firm-level) on the firm-level line
import React from "react";
import { fmtNumber, fmtPlural } from "@/lib/format";
import { useI18n } from "@/contexts/I18nContext";   // STEP 2 · A8: dict.plurals (fmtPlural, the app's one plural helper)
import { FD, currencyBadge, overrunTone, TARGET, fill, fmtHours } from "./fdTheme";
import { FdLink, Panel, Td, Chip, Empty } from "./FdPrimitives";
import { FdResizableTable, FdRTh } from "./FdResizable";   // Step 11d · add-on: adjustable columns
import { useResizableColumns } from "@/components/ui/resizable-table";

// Step 11d · add-on: px defaults in the old proportions (20/6/11/9/9/11/12/11/11 % of 1,150 px), adjustable per browser
const COLS_PX = [230, 72, 127, 104, 104, 127, 138, 127, 127];
const COLS_MIN = [140, 60, 90, 80, 80, 90, 90, 90, 90];
const TONE = { green: FD.green, red: FD.red, grey: FD.grey };
const isNum = (v) => typeof v === "number" && Number.isFinite(v);

export default function FdBilling({ rows, totals, aiFirm, aiTotal, aiRate, t, lang }) {
  const billCols = useResizableColumns({ storageKey: "dashboard-billing", defaults: COLS_PX, mins: COLS_MIN });
  const { dict } = useI18n();
  const list = Array.isArray(rows) ? rows : [];
  const n = (v, d = 0) => fmtNumber(v || 0, lang, { minimumFractionDigits: d, maximumFractionDigits: d });
  const h = (v) => fmtHours(v, lang, t, { unit: false });
  const hu = (v) => fmtHours(v, lang, t);
  const ai = (a) => {
    // STEP 2 · A8: "1 unit" / "2 units" / "0 units" (+ the Arabic forms); the count shown whole, the plural chosen on it
    const units = fmtPlural(Math.round(Number((a && a.units) || 0)), lang, dict.plurals.aiUnits);
    return `${units} · ${a && isNum(a.usd) ? fill(t("fd.billing.aiUsd"), { n: n(a.usd, 2) }) : t("fd.billing.aiRateNotSet")}`;
  };
  // D-2 / D-3: the margin cell — a figure only when the API sends one (margin_status "ok")
  const margin = (r, testid) => {
    const st = r.margin_status;
    if (st === "incomplete") {
      const uh = (r.margin_incomplete && r.margin_incomplete.unrated_hours) ?? r.unrated_hours;
      return <span data-testid={testid} data-status="incomplete" className="text-xs font-semibold" style={{ color: FD.amber }}>{fill(t("fd.billing.incomplete"), { h: hu(uh) })}</span>;
    }
    if (st === "fee_not_set") return <span data-testid={testid} data-status="fee_not_set" className="text-xs" style={{ color: FD.muted }}>{t("fd.billing.feeNotSet")}</span>;
    const m = r.margin ?? r.fee_minus_cost;
    if (!isNum(m)) return <span data-testid={testid} style={{ color: FD.muted }}>—</span>;
    return <span data-testid={testid} data-status="ok" style={{ color: m < 0 ? FD.red : FD.text }}>{n(m)}</span>;
  };
  const feeSet = (r) => r.fee_set !== false && isNum(r.fee);
  const curLabel = (cur) => (!cur || cur === "unspecified" ? t("fd.billing.unspecified") : cur);
  const totalRows = Object.entries(totals || {});
  const tot = { background: FD.thBg, borderTop: `2px solid ${FD.thLine}` };
  const rate = aiRate && aiRate.configured && isNum(aiRate.usd_per_unit) ? aiRate.usd_per_unit : null;
  return (
    <Panel title={t("fd.billing.title")} testid="fd-billing">
      <FdResizableTable cols={billCols} testid="fd-billing-table">
        <thead>
          <tr>
            <FdRTh index={0}>{t("fd.billing.client")}</FdRTh><FdRTh index={1}>{t("fd.billing.cur")}</FdRTh><FdRTh index={2} align="end">{t("fd.billing.fee")}</FdRTh>
            <FdRTh index={3} align="end">{t("fd.billing.budget")}</FdRTh><FdRTh index={4} align="end">{t("fd.billing.actual")}</FdRTh><FdRTh index={5} align="end">{t("fd.billing.cost")}</FdRTh>
            <FdRTh index={6}>{t("fd.billing.ai")}</FdRTh><FdRTh index={7} align="end">{t("fd.billing.margin")}</FdRTh><FdRTh index={8}>{t("fd.billing.overrun")}</FdRTh>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && <tr><Td colSpan={9}><Empty>{t("fd.billing.empty")}</Empty></Td></tr>}
          {list.map((r) => {
            const id = r.engagement_id; const cb = currencyBadge(r.currency); const tone = overrunTone(r);
            const budget = r.budgeted_hours ?? r.budget_hours, actual = r.hours_spent ?? r.actual_hours;
            const cost = r.cost ?? r.cost_to_date;
            const over = r.overrun && r.overrun.hours !== undefined ? Number(r.overrun.hours) : Math.max(0, Number(actual || 0) - Number(budget || 0));
            const L = (children, extra) => <FdLink to={TARGET.billing} engId={id} {...extra}>{children}</FdLink>;
            return (
              <tr key={id}>
                <Td>{L(r.client_name, { testid: `fd-bill-row-${id}`, className: "font-medium" })}</Td>
                <Td>{L(<Chip bg={cb.bg} fg={cb.fg}>{curLabel(r.currency)}</Chip>)}</Td>
                <Td align="end">{L(feeSet(r) ? <span data-testid={`fd-bill-fee-${id}`}>{n(r.fee)}</span>
                  : <span data-testid={`fd-bill-fee-${id}`} data-status="fee_not_set" className="text-xs" style={{ color: FD.muted }}>{t("fd.billing.feeNotSet")}</span>, { className: "tabular-nums" })}</Td>
                <Td align="end">{L(r.budget_set === false ? "—" : h(budget), { className: "tabular-nums" })}</Td>
                <Td align="end">{L(<span>{h(actual)}{Number(r.unrated_hours || 0) > 0 && (
                  <span data-testid={`fd-unrated-${id}`} className="block text-xs font-normal" style={{ color: FD.muted }}>
                    {fill(t("fd.billing.rateNotSet"), { h: hu(r.unrated_hours) })}</span>)}</span>, { className: "tabular-nums" })}</Td>
                <Td align="end">{L(n(cost), { className: "tabular-nums" })}</Td>
                <Td>{L(<span data-testid={`fd-bill-ai-${id}`}>{ai(r.ai)}</span>, { className: "tabular-nums text-xs" })}</Td>
                <Td align="end">{L(margin(r, `fd-bill-margin-${id}`), { className: "tabular-nums" })}</Td>
                <Td>
                  {L(<span data-tone={tone} data-testid={`fd-overrun-${id}`} className="font-semibold">
                    {tone === "grey" ? t("fd.billing.noBudget") : tone === "red" ? fill(t("fd.billing.over"), { h: hu(over) }) : t("fd.billing.within")}
                  </span>, { style: { color: TONE[tone] } })}
                </Td>
              </tr>
            );
          })}
          {totalRows.map(([cur, tt]) => (
            <tr key={`total-${cur}`} data-testid={`fd-bill-total-${cur}`}>
              <Td className="font-semibold" style={tot}>
                {fill(t("fd.billing.total"), { cur: curLabel(cur) })}
                {tt.engagements !== undefined ? <span className="ms-1 text-xs font-normal" style={{ color: FD.muted }}>({fill(t("fd.billing.nEng"), { n: n(tt.engagements) })})</span> : null}
              </Td>
              <Td style={tot}>{curLabel(cur)}</Td>
              <Td align="end" className="font-semibold tabular-nums" style={tot}>
                <span data-testid={`fd-bill-totfee-${cur}`}>
                  {isNum(tt.fee) ? n(tt.fee) : <span className="text-xs font-normal" style={{ color: FD.muted }}>{t("fd.billing.feeNotSet")}</span>}
                  {isNum(tt.fee) && Number(tt.fee_not_set || 0) > 0
                    ? <span className="block text-xs font-normal" style={{ color: FD.muted }}>{fill(t("fd.billing.feeNotSetN"), { n: n(tt.fee_not_set) })}</span> : null}
                </span>
              </Td>
              <Td align="end" className="tabular-nums" style={tot}>{tt.budgeted_hours !== undefined ? h(tt.budgeted_hours) : "—"}</Td>
              <Td align="end" className="tabular-nums" style={tot}>{h(tt.hours_spent ?? tt.actual_hours)}</Td>
              <Td align="end" className="tabular-nums" style={tot}>{n(tt.cost ?? tt.cost_to_date)}</Td>
              <Td className="tabular-nums text-xs" style={tot}>{tt.ai ? ai(tt.ai) : "—"}</Td>
              <Td align="end" className="font-semibold tabular-nums" style={tot}>
                {tt.margin_status === "fee_not_set" && isNum(tt.fee)
                  ? <span data-testid={`fd-bill-totmargin-${cur}`} data-status="fee_not_set" className="text-xs font-normal" style={{ color: FD.muted }}>{fill(t("fd.billing.feeNotSetN"), { n: n(tt.fee_not_set) })}</span>
                  : margin(tt, `fd-bill-totmargin-${cur}`)}
              </Td>
              <Td style={tot} />
            </tr>
          ))}
          <tr>
            <Td colSpan={9} className="text-xs" style={{ color: FD.muted }}>
              <FdLink to={TARGET.aiUsage} testid="fd-bill-ai-firm" className="!inline italic">{t("fd.billing.aiFirm")}: {ai(aiFirm)}</FdLink>
              {aiTotal ? (
                <span data-testid="fd-bill-ai-total" className="ms-3 font-semibold" style={{ color: FD.text }}>
                  {t("fd.billing.aiTotal")}: {ai(aiTotal)}
                  {aiTotal.not_listed && Number(aiTotal.not_listed.units || 0) > 0
                    ? <span className="ms-1 font-normal" style={{ color: FD.muted }}>({fill(t("fd.billing.aiNotListed"), { x: ai(aiTotal.not_listed) })})</span> : null}
                </span>
              ) : null}
              {aiRate ? (
                <span data-testid="fd-bill-ai-rate" className="ms-3">
                  {rate !== null ? fill(t("fd.billing.aiRate"), { r: fmtNumber(rate, lang, { minimumFractionDigits: 2, maximumFractionDigits: 6 }) }) : t("fd.billing.aiRateNotSet")}
                </span>
              ) : null}
            </Td>
          </tr>
        </tbody>
      </FdResizableTable>
    </Panel>
  );
}
